import type { PointerEvent, RefObject } from 'react'

import { useCallback, useRef } from 'react'

type GlassSheen<T extends HTMLElement> = {
    ref: RefObject<T | null>
    onPointerMove: (event: PointerEvent<HTMLElement>) => void
    onPointerLeave: () => void
}

// Moves the specular sheen of a `.glass` surface towards the pointer.
// Attach `ref` to the glass element and the handlers to whichever element receives the pointer.
export const useGlassSheen = <T extends HTMLElement = HTMLDivElement>(): GlassSheen<T> => {
    const ref = useRef<T>(null)

    const onPointerMove = useCallback((event: PointerEvent<HTMLElement>) => {
        const element = ref.current
        if (!element || event.pointerType === 'touch') return

        const rect = element.getBoundingClientRect()
        const x = ((event.clientX - rect.left) / rect.width) * 100
        const y = ((event.clientY - rect.top) / rect.height) * 100

        element.style.setProperty('--glass-x', `${x.toFixed(1)}%`)
        element.style.setProperty('--glass-y', `${y.toFixed(1)}%`)
    }, [])

    const onPointerLeave = useCallback(() => {
        const element = ref.current
        if (!element) return

        element.style.removeProperty('--glass-x')
        element.style.removeProperty('--glass-y')
    }, [])

    return { ref, onPointerMove, onPointerLeave }
}
